import { useState, useEffect, useMemo } from 'react';
import FloatingLabelInput from 'components/ui/FloatingLabelInput';
import Select from 'components/ui/Select';
import Card from 'components/ui/Card';
import BaseButton from 'components/ui/BaseButton';
import { Copy } from 'assets/icons/revamp';
import { t } from 'services/i18n.service';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { copyActivitySelector } from 'store/reducer/setting/selectors';
import { copyActivity } from 'store/reducer/setting/slice';
import { getCopiedActivity } from 'utils/settings.util';
import { getRoutineActivities } from 'utils/helpers';
import {
  resolveRoutineDestination,
  toCustomRoutineOptionValue
} from 'utils/routine-destination.util';
import { TOAST } from 'constants/general';
import { toast } from 'react-toastify';

const CopyActivity: React.FC = () => {
  const dispatch = useAppDispatch();
  const { activity, moreOptions, currentSettings } =
    useAppSelectorShallow(copyActivitySelector);
  const customRoutines = currentSettings.custom_routines;

  const [copyName, setCopyName] = useState('');
  const [destinationRoutine, setDestinationRoutine] = useState('');

  // Initialize copy name when activity changes
  useEffect(() => {
    if (activity?.name) {
      setCopyName(`${activity.name} (copy)`);
    }
  }, [activity?.name]);

  // Build destination options: built-in routines + custom routines
  const destinationOptions = useMemo(() => {
    // Get built-in routine options based on current activity type
    const builtIns = getRoutineActivities(moreOptions.type).map((src) => ({
      label: t(src.label),
      value: src.value
    }));

    // Get custom routine options
    const dropDownOptionsForCustomRoutines = (customRoutines ?? []).map(
      (routine) => ({
        label: routine.name || t('custom_routine'),
        value: toCustomRoutineOptionValue(routine.id)
      })
    );

    return [...builtIns, ...dropDownOptionsForCustomRoutines];
  }, [moreOptions.type, customRoutines]);

  const handleCreateCopy = () => {
    if (!copyName || !destinationRoutine || !activity) {
      return;
    }
    const destination = resolveRoutineDestination(
      destinationRoutine,
      currentSettings
    );
    if (!destination) {
      return;
    }

    // Create copied activity with custom name
    const copiedActivity = {
      ...getCopiedActivity(activity),
      name: copyName
    };

    dispatch(copyActivity({ activity: copiedActivity, destination }));

    toast.info(t('copied'), { autoClose: TOAST.AUTO_CLOSE.COPIED });

    // Reset form
    setCopyName(`${activity.name} (copy)`);
    setDestinationRoutine('');
  };

  return (
    <Card className='flex flex-col ~gap-4/6'>
      <div className='flex items-start ~gap-2/3'>
        <Copy
          styles='~w-3/4 ~h-3/4 flex-shrink-0 text-primary-500 dark:text-primary-dark-500 ~mt-0.5/1'
          fill='currentColor'
          fillDarkMode='currentColor'
        />
        <h3 className='~text-xs/sm font-semibold text-black dark:text-white flex flex-col'>
          {t('tabs_revamp.advanced_tab.make_a_copy')}
          <span className='~text-[0.625rem]/xs text-gray-600 dark:text-gray-400 font-normal'>
            {t('tabs_revamp.advanced_tab.make_a_copy_description')}
          </span>
        </h3>
      </div>
      <div className='flex flex-col ~gap-3/4 ~ml-0/0'>
        <FloatingLabelInput
          type='text'
          placeholder={t('tabs_revamp.advanced_tab.copy_name')}
          value={copyName}
          onChange={(e) => setCopyName(e.target.value)}
          size='small'
          variant='default'
          showLabel={true}
        />
        <div className='flex items-center ~gap-2/3'>
          <div className='flex-1'>
            <Select
              value={destinationRoutine}
              options={destinationOptions}
              onChange={(value) => setDestinationRoutine(value as string)}
              placeholder={t('tabs_revamp.advanced_tab.select_a_routine')}
              className='w-full ~min-h-4/8'
              isFullWidth={true}
              size='sm'
            />
          </div>
          <BaseButton
            title={t('tabs_revamp.advanced_tab.create_copy')}
            onClick={handleCreateCopy}
            size='xs'
            icon={
              <Copy
                styles={`~w-3/3.5 ~h-3/3.5 text-primary-light-900 dark:text-primary-dark-500 ${destinationRoutine ? 'text-primary-light-900 dark:text-primary-dark-500' : 'text-secondary-dark-900 dark:text-secondary-dark-500'}`}
                fill='currentColor'
                fillDarkMode='currentColor'
              />
            }
            disabled={!copyName || !destinationRoutine}
          />
        </div>
      </div>
    </Card>
  );
};

export default CopyActivity;
